/**
 * Flutterwave Payment Configuration — Single Source of Truth
 *
 * This file defines which payment methods are available per country.
 * It is consumed by:
 *   - PaymentMethodModal (UI dropdown / method selector)
 *   - PricingTable (routing logic)
 *   - Backend API routes (bank transfer / MoMo / OPay initiation)
 *   - AlternatePaymentOptions component ("Having trouble?" section)
 *
 * Adding a new country / method:
 *   1. Add the entry here.
 *   2. For Bank Transfer, add the initiator in create-bank-transfer/route.ts.
 *   3. For MoMo, ensure the country is in MOMO_COUNTRY_CONFIG (momo.ts).
 *   4. For OPay, it is Nigeria-only — no changes needed.
 */

// ── Payment method identifiers ────────────────────────────────────────
export const PAYMENT_METHOD = {
  BANK_TRANSFER: "bank_transfer",
  MOMO: "momo",
  OPAY: "opay",
} as const;

export type PaymentMethodId = (typeof PAYMENT_METHOD)[keyof typeof PAYMENT_METHOD];

// ── Per-country enabled methods ───────────────────────────────────────
export interface CountryPaymentConfig {
  /** Methods available for this country, in display order */
  methods: PaymentMethodId[];
  /** ISO 4217 currency code */
  currency: string;
  /** Human-readable country name */
  name: string;
  /** Default method when user has not chosen yet */
  defaultMethod: PaymentMethodId;
}

/**
 * Master config: country-code → enabled Flutterwave payment methods.
 *
 * Order of `methods` determines display order in the "choose method" selector.
 * `defaultMethod` is the pre-selected radio when the selector first renders.
 */
export const COUNTRY_PAYMENT_CONFIG: Record<string, CountryPaymentConfig> = {
  // ── Nigeria ─────────────────────────────────────────────────────────
  NG: {
    methods: [PAYMENT_METHOD.BANK_TRANSFER, PAYMENT_METHOD.OPAY],
    currency: "NGN",
    name: "Nigeria",
    defaultMethod: PAYMENT_METHOD.BANK_TRANSFER,
  },

  // ── Ghana ───────────────────────────────────────────────────────────
  GH: {
    methods: [PAYMENT_METHOD.MOMO, PAYMENT_METHOD.BANK_TRANSFER],
    currency: "GHS",
    name: "Ghana",
    defaultMethod: PAYMENT_METHOD.MOMO,
  },

  // ── Kenya ───────────────────────────────────────────────────────────
  KE: {
    methods: [PAYMENT_METHOD.MOMO, PAYMENT_METHOD.BANK_TRANSFER],
    currency: "KES",
    name: "Kenya",
    defaultMethod: PAYMENT_METHOD.MOMO,
  },

  // ── Uganda ──────────────────────────────────────────────────────────
  UG: {
    methods: [PAYMENT_METHOD.MOMO],
    currency: "UGX",
    name: "Uganda",
    defaultMethod: PAYMENT_METHOD.MOMO,
  },

  // ── Rwanda ──────────────────────────────────────────────────────────
  RW: {
    methods: [PAYMENT_METHOD.MOMO],
    currency: "RWF",
    name: "Rwanda",
    defaultMethod: PAYMENT_METHOD.MOMO,
  },

  // ── Zambia ──────────────────────────────────────────────────────────
  ZM: {
    methods: [PAYMENT_METHOD.MOMO],
    currency: "ZMW",
    name: "Zambia",
    defaultMethod: PAYMENT_METHOD.MOMO,
  },

  // ── Tanzania ────────────────────────────────────────────────────────
  TZ: {
    methods: [PAYMENT_METHOD.MOMO],
    currency: "TZS",
    name: "Tanzania",
    defaultMethod: PAYMENT_METHOD.MOMO,
  },

  // ── South Africa ────────────────────────────────────────────────────
  ZA: {
    methods: [PAYMENT_METHOD.BANK_TRANSFER],
    currency: "ZAR",
    name: "South Africa",
    defaultMethod: PAYMENT_METHOD.BANK_TRANSFER,
  },

  // ── Egypt ───────────────────────────────────────────────────────────
  EG: {
    methods: [PAYMENT_METHOD.BANK_TRANSFER],
    currency: "EGP",
    name: "Egypt",
    defaultMethod: PAYMENT_METHOD.BANK_TRANSFER,
  },

  // ── Francophone West Africa (region, not a single country) ──────────
  XOF: {
    methods: [PAYMENT_METHOD.MOMO],
    currency: "XOF",
    name: "Francophone West Africa",
    defaultMethod: PAYMENT_METHOD.MOMO,
  },

  // ── Francophone Central Africa (region) ─────────────────────────────
  XAF: {
    methods: [PAYMENT_METHOD.MOMO],
    currency: "XAF",
    name: "Francophone Central Africa",
    defaultMethod: PAYMENT_METHOD.MOMO,
  },

  // ── Francophone individual countries (map to XOF/XAF rails internally) ──
  BF: {
    methods: [PAYMENT_METHOD.MOMO],
    currency: "XOF",
    name: "Burkina Faso",
    defaultMethod: PAYMENT_METHOD.MOMO,
  },
  CI: {
    methods: [PAYMENT_METHOD.MOMO],
    currency: "XOF",
    name: "Côte d'Ivoire",
    defaultMethod: PAYMENT_METHOD.MOMO,
  },
  SN: {
    methods: [PAYMENT_METHOD.MOMO],
    currency: "XOF",
    name: "Senegal",
    defaultMethod: PAYMENT_METHOD.MOMO,
  },
  CM: {
    methods: [PAYMENT_METHOD.MOMO],
    currency: "XAF",
    name: "Cameroon",
    defaultMethod: PAYMENT_METHOD.MOMO,
  },
};

// ── Helper functions ──────────────────────────────────────────────────

/**
 * Get payment config for a country. Returns `null` for unsupported countries.
 */
export function getCountryPaymentConfig(
  countryCode: string,
): CountryPaymentConfig | null {
  return COUNTRY_PAYMENT_CONFIG[countryCode?.toUpperCase()] ?? null;
}

/**
 * Check whether a specific method is enabled for a country.
 */
export function isMethodEnabled(
  countryCode: string,
  method: PaymentMethodId,
): boolean {
  const cfg = getCountryPaymentConfig(countryCode);
  return cfg ? cfg.methods.includes(method) : false;
}

/**
 * Get list of country codes that support a given method.
 */
export function getCountriesForMethod(method: PaymentMethodId): string[] {
  return Object.entries(COUNTRY_PAYMENT_CONFIG)
    .filter(([, cfg]) => cfg.methods.includes(method))
    .map(([code]) => code);
}

/**
 * Countries that support MoMo (for dropdown filtering).
 */
export function getMomoSupportedCountries(): string[] {
  return getCountriesForMethod(PAYMENT_METHOD.MOMO);
}

/**
 * Countries that support Bank Transfer.
 */
export function getBankTransferSupportedCountries(): string[] {
  return getCountriesForMethod(PAYMENT_METHOD.BANK_TRANSFER);
}

/**
 * Does this country have more than one Flutterwave method?
 * If so, the UI should show a method-selector before generating payment details.
 */
export function hasMultipleMethods(countryCode: string): boolean {
  const cfg = getCountryPaymentConfig(countryCode);
  return cfg ? cfg.methods.length > 1 : false;
}

/**
 * Get label + emoji for a payment method, used in UI.
 */
export function getMethodDisplay(method: PaymentMethodId): {
  label: string;
  emoji: string;
  description: string;
} {
  switch (method) {
    case PAYMENT_METHOD.BANK_TRANSFER:
      return {
        label: "Bank Transfer",
        emoji: "🏦",
        description: "Shows bank account details to transfer to.",
      };
    case PAYMENT_METHOD.MOMO:
      return {
        label: "Mobile Money",
        emoji: "📱",
        description: "Requires a local phone number and network selection.",
      };
    case PAYMENT_METHOD.OPAY:
      return {
        label: "OPay Wallet",
        emoji: "🪙",
        description: "Pay from your OPay mobile wallet (Nigeria only).",
      };
    default:
      return { label: method, emoji: "💳", description: "" };
  }
}
